import "./index.css";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import { useCreateRerate } from "~/features/rerate/hooks/use-post-rerate.ts";
import { StatusBanner } from "~/shared/components/status-banner/status-banner.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { TraxPageLayout } from "~/shared/components/trax-page-layout/trax-page-layout.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import { useSharedUserFilter } from "~/shared/hooks/use-shared-user-filter.ts";
import type {
	CustomerQuoteBoardItem,
	QuoteBoardItem,
} from "~/shared/schemas/quote-board-schema.ts";
import type {
	GetQuoteBoardType,
	QuoteBoardChip,
} from "~/shared/types/quote-board-types.ts";
import type { GridPrefColumn } from "~/shared/utils/apply-grid-prefs.ts";
import { getDefaultQuoteBoardGridPrefs } from "~/shared/utils/default-grid-prefs.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import { useGetInternalUserPreferences } from "../profile/hooks/internal/use-get-internal-user-preferences.ts";
import { useInternalGridPreferencesSync } from "../profile/hooks/internal/use-internal-grid-preferences-sync.ts";
import { useGetUserPreferences } from "../profile/hooks/use-get-user-preferences.ts";
import { useGridPreferencesSync } from "../profile/hooks/use-grid-preferences-sync.ts";
import { BulkQuoteBatchStatus } from "./components/bulk-quote-batch-status/bulk-quote-batch-status.tsx";
import { BulkTenderCarrierSelectionModal } from "./components/bulk-tender-carrier-selection-modal/bulk-tender-carrier-selection-modal.tsx";
import type { BulkTenderInsuranceBoardQuote } from "./components/bulk-tender-insurance-modal/bulk-tender-insurance-modal.tsx";
import { BulkTenderInsuranceModal } from "./components/bulk-tender-insurance-modal/bulk-tender-insurance-modal.tsx";
import { BulkTenderModal } from "./components/bulk-tender-modal/bulk-tender-modal.tsx";
import { BulkTenderToolbar } from "./components/bulk-tender-toolbar/bulk-tender-toolbar.tsx";
import { useBulkQuoteBatchBoardStatus } from "./hooks/use-bulk-quote-batch-status.ts";
import { useBulkTenderSelection } from "./hooks/use-bulk-tender-selection.ts";
import { useQuoteBoardColumns } from "./hooks/use-quote-board-columns.ts";
import { useQuoteBoardFilter } from "./hooks/use-quote-board-filter.ts";
import { useGetQuoteBoardItems as useGetQuoteBoardItemsV2 } from "./hooks/use-quote-board-hooks.ms2.ts";
import { useRequoteQuote } from "./hooks/use-requote-quote.ts";
import { useUnprocessedBulkQuotes } from "./hooks/use-unprocessed-bulk-quotes.ts";
import { QuoteBoardCardList } from "./quote-board-card/quote-board-card-list.tsx";
import { QuoteBoardGrid } from "./quote-board-grid/quote-board-grid.tsx";
import { QuoteBoardHeader as QuoteBoardHeaderV2 } from "./quote-board-header/quote-board-header.ms2.tsx";
import type { BulkTenderNotificationTarget } from "./schemas/bulk-tender-schema.ts";
import { applyBulkTenderFeatureGate } from "./utils/bulk-tender-feature-gate.ts";
import { validateBulkTenderSelection } from "./utils/bulk-tender-preflight.ts";
import { buildBulkTenderRerateUrl } from "./utils/quote-board-return-url.ts";
import {
	canRequoteQuoteBoardRow,
	canTenderQuoteBoardRow,
} from "./utils/quote-board-status.ts";

const DEFAULT_CHIP = { text: "", value: "", removable: true };

const getBulkInsuranceState = (
	insuranceState: Record<number, boolean>,
	quoteId: number,
	quote?: { insuranceApplyTypeId?: number | null },
): boolean => insuranceState[quoteId] ?? quote?.insuranceApplyTypeId != null;

const applyBulkInsuranceStateChange = (
	quoteId: number | null,
	insuranceApplied: boolean | undefined,
	update: (quoteId: number, insuranceApplied: boolean) => void,
): void => {
	if (quoteId != null && insuranceApplied != null) {
		update(quoteId, insuranceApplied);
	}
};

const getBulkRerateTitle = (selectedCount: number): string => {
	if (selectedCount === 0) return "Select one eligible shipment to rerate";
	if (selectedCount === 1) return "Rerate selected shipment";
	return "Select exactly one eligible shipment to rerate";
};

const isFeatureAvailable = (isReady: boolean, isEnabled: boolean): boolean =>
	isReady && isEnabled;

const getEnabledQuoteAction = (
	isEnabled: boolean,
	action: (quoteId: number) => void,
): ((quoteId: number) => void) | undefined => (isEnabled ? action : undefined);

const getBulkTenderSelectionItems = (
	items: (QuoteBoardItem | CustomerQuoteBoardItem)[],
	isBulkTenderAvailable: boolean,
): (QuoteBoardItem | CustomerQuoteBoardItem)[] =>
	isBulkTenderAvailable ? items : [];

// The grid applies this same Bulk/Individual split internally; the mobile
// card list has no equivalent, so it must be filtered before being passed in.
const filterByQuoteType = (
	items: (QuoteBoardItem | CustomerQuoteBoardItem)[],
	quoteType: string,
): (QuoteBoardItem | CustomerQuoteBoardItem)[] => {
	if (quoteType === "Bulk") {
		return items.filter((item) => item.bulkQuoteId != null);
	}
	if (quoteType === "Individual") {
		return items.filter((item) => item.bulkQuoteId == null);
	}
	return items;
};

const shouldShowBulkTenderActions = (
	items: (QuoteBoardItem | CustomerQuoteBoardItem)[],
	quoteType: string,
	isBulkTenderAvailable: boolean,
): boolean =>
	isBulkTenderAvailable &&
	quoteType === "Bulk" &&
	items.some((item) => item.bulkQuoteId != null);

const RequoteStatusBanner = ({ quoteId }: { quoteId?: number }) => (
	<StatusBanner
		title={
			quoteId == null ? "Requoting shipment" : `Requoting quote ${quoteId}`
		}
	>
		<span>
			Rerating with carriers. The replacement quote will appear here
			automatically.
		</span>
	</StatusBanner>
);

const getVisibleColumnIds = (columns: GridPrefColumn[]): string[] =>
	columns.filter((column) => column.isVisible).map((column) => column.columnId);

const getBulkTenderToolbarWrapperClassName = (isMobile: boolean): string =>
	isMobile
		? "bulk-tender-toolbar-wrapper bulk-tender-toolbar-wrapper-sticky"
		: "bulk-tender-toolbar-wrapper";

const hasSameColumnIds = (first: string[], second: string[]): boolean => {
	if (first.length !== second.length) return false;

	const secondColumnIds = new Set(second);
	return first.every((columnId) => secondColumnIds.has(columnId));
};

const shouldResetQuoteBoardDefaults = (
	columnPrefs: GridPrefColumn[] | undefined,
	quoteType: string,
): boolean => {
	if (!columnPrefs?.length) return false;

	const otherQuoteType = quoteType === "Bulk" ? "Individual" : "Bulk";
	const otherDefaults =
		getDefaultQuoteBoardGridPrefs(false, otherQuoteType)[
			GridIdentifiers["Quote Board"]
		]?.columns ?? [];

	return hasSameColumnIds(
		getVisibleColumnIds(columnPrefs),
		getVisibleColumnIds(otherDefaults),
	);
};

type QuoteBoardColumnOptions = {
	isLoading: boolean;
	isInternal: boolean;
	columnPrefs?: GridPrefColumn[];
	quoteType: string;
	isBulkTenderAvailable: boolean;
	hasInsurance?: (quoteId: number) => boolean;
	onOpenCarrierSelection: (quoteId: number) => void;
	onOpenBulkCarrierSelection?: (quoteId: number) => void;
	onOpenInsurance?: (quoteId: number) => void;
};

const getQuoteBoardColumnOptions = ({
	isLoading,
	isInternal,
	columnPrefs,
	quoteType,
	isBulkTenderAvailable,
	hasInsurance,
	onOpenCarrierSelection,
	onOpenBulkCarrierSelection,
	onOpenInsurance,
}: QuoteBoardColumnOptions) => ({
	isLoading,
	isInternal,
	columnPrefs,
	onOpenCarrierSelection,
	onOpenBulkCarrierSelection,
	onOpenInsurance,
	hasInsurance,
	quoteType,
	isBulkTenderAvailable,
});

type QuoteBoardModalsProps = {
	bulkTenderEnabled: boolean;
	insuranceQuoteId: number | null;
	initialInsuranceQuote?: BulkTenderInsuranceBoardQuote;
	showBulkModal: boolean;
	showBulkTenderModal: boolean;
	carrierSelectionQuoteId: number | null;
	quoteIds: number[];
	notificationTargets: readonly BulkTenderNotificationTarget[];
	unprocessedCount?: string | number;
	onCloseBulkModal: () => void;
	onCloseBulkTenderModal: () => void;
	onCompleteBulkTender: (tenderedQuoteIds: number[]) => void;
	onPreflightBulkTender: () => ReturnType<typeof validateBulkTenderSelection>;
	onBulkTenderResultAction: (quoteId: number, reasonCode: string) => void;
	onCloseCarrierSelection: () => void;
	onCompleteCarrierSelection: () => void;
	onCloseInsurance: () => void;
	onCompleteInsurance: (insuranceApplied?: boolean) => void;
	onInsuranceStateChange: (quoteId: number, insuranceApplied: boolean) => void;
};

const QuoteBoardModals = ({
	bulkTenderEnabled,
	insuranceQuoteId,
	initialInsuranceQuote,
	showBulkModal,
	showBulkTenderModal,
	carrierSelectionQuoteId,
	quoteIds,
	notificationTargets,
	unprocessedCount,
	onCloseBulkModal,
	onCloseBulkTenderModal,
	onCompleteBulkTender,
	onPreflightBulkTender,
	onBulkTenderResultAction,
	onCloseCarrierSelection,
	onCompleteCarrierSelection,
	onCloseInsurance,
	onCompleteInsurance,
	onInsuranceStateChange,
}: QuoteBoardModalsProps) => {
	return (
		<>
			{bulkTenderEnabled && showBulkTenderModal ? (
				<BulkTenderModal
					notificationTargets={notificationTargets}
					onClose={onCloseBulkTenderModal}
					onComplete={onCompleteBulkTender}
					onPreflight={onPreflightBulkTender}
					onResultAction={onBulkTenderResultAction}
					quoteIds={quoteIds}
				/>
			) : null}
			{!bulkTenderEnabled || carrierSelectionQuoteId == null ? null : (
				<BulkTenderCarrierSelectionModal
					onClose={onCloseCarrierSelection}
					onComplete={onCompleteCarrierSelection}
					quoteId={carrierSelectionQuoteId}
				/>
			)}
			{!bulkTenderEnabled || insuranceQuoteId == null ? null : (
				<BulkTenderInsuranceModal
					initialQuote={initialInsuranceQuote}
					onClose={onCloseInsurance}
					onComplete={onCompleteInsurance}
					onInsuranceStateChange={onInsuranceStateChange}
					quoteId={insuranceQuoteId}
				/>
			)}
			{showBulkModal ? (
				<TraxModal
					confirmText={"Ok"}
					onCancel={onCloseBulkModal}
					onClose={onCloseBulkModal}
					onConfirm={onCloseBulkModal}
					showCancelButton={false}
					showConfirmationButtons={true}
					title={"Unprocessed Bulk Quotes"}
				>
					<div style={{ padding: "1rem" }}>
						You have {unprocessedCount} unprocessed bulk uploaded quotes. Please
						refresh the page to view your processed quotes.
					</div>
				</TraxModal>
			) : null}
		</>
	);
};

export const QuoteBoard = () => {
	const [searchParams] = useSearchParams();
	const location = useLocation();
	const navigate = useNavigate();
	const { showError, showSuccess, showWarning } = useNotificationActions();
	const { mutate: createRerate } = useCreateRerate();
	const {
		mutate: requoteQuote,
		isPending: isRequoting,
		variables: requotingQuoteId,
	} = useRequoteQuote();
	// Identifies the batch to surface after an upload; it does not filter the board.
	const focusedBatchId = searchParams.get("focusBatch");
	const requestedQuoteType = searchParams.get("quoteType");
	const [bulkInsuranceApplied, setBulkInsuranceApplied] = useState<
		Record<number, boolean>
	>({});
	useEffect(() => {
		setBulkInsuranceApplied({});
	}, [focusedBatchId]);
	const [carrierSelectionQuoteId, setCarrierSelectionQuoteId] = useState<
		number | null
	>(null);
	const [insuranceQuoteId, setInsuranceQuoteId] = useState<number | null>(null);
	const [showBulkTenderModal, setShowBulkTenderModal] = useState(false);
	const [tenderedQuoteIds, setTenderedQuoteIds] = useState<number[]>([]);
	const handleOpenCarrierSelection = useCallback(
		(quoteId: number) => setCarrierSelectionQuoteId(quoteId),
		[],
	);
	const handleOpenInsurance = useCallback(
		(quoteId: number) => setInsuranceQuoteId(quoteId),
		[],
	);
	const handleInsuranceStateChange = useCallback(
		(quoteId: number, insuranceApplied: boolean) => {
			setBulkInsuranceApplied((current) => ({
				...current,
				[quoteId]: insuranceApplied,
			}));
		},
		[],
	);
	const { isReady, isEnabled } = useFeatureFlagGate("traxLtlQuoteBoardv2");
	const { isEnabled: isBulkUploadEnabled } =
		useFeatureFlagGate("traxLtlBulkUpload");
	const { isReady: isBulkTenderReady, isEnabled: isBulkTenderEnabled } =
		useFeatureFlagGate("traxLtlBulkTender");
	const isBulkTenderAvailable = isFeatureAvailable(
		isBulkTenderReady,
		isBulkTenderEnabled,
	);
	useEffect(() => {
		if (isBulkTenderAvailable) return;

		setBulkInsuranceApplied({});
		setCarrierSelectionQuoteId(null);
		setInsuranceQuoteId(null);
		setShowBulkTenderModal(false);
	}, [isBulkTenderAvailable]);
	const { isInternal } = useDeploymentType();
	// External-only per US 547724 — internal keeps the grid at every width.
	const isMobile = useIsMobile(768) && !isInternal;
	const handleOpenRerate = useCallback(
		(quoteId: number) => {
			const navigateToRerate = (rerateQuoteId: number) => {
				navigate(
					buildBulkTenderRerateUrl(
						rerateQuoteId,
						location.pathname,
						location.search,
					),
				);
			};

			createRerate(
				{
					quoteId,
					shipmentData: null,
				},
				{
					onSuccess: (data) => navigateToRerate(data.id),
					onError: () =>
						showError("Unable to start a rerate. Please try again."),
				},
			);
		},
		[createRerate, location.pathname, location.search, navigate, showError],
	);

	const { data: unprocessedCount } = useUnprocessedBulkQuotes();
	const [showBulkModal, setShowBulkModal] = useState(false);

	useEffect(() => {
		if (
			isBulkUploadEnabled &&
			typeof unprocessedCount === "number" &&
			unprocessedCount > 0
		) {
			setShowBulkModal(true);
		}
	}, [unprocessedCount, isBulkUploadEnabled]);

	const [chips, setChips] = useState<QuoteBoardChip[]>([]);
	const [gridResetKey, setGridResetKey] = useState(0);
	const [timeFrame, setTimeFrame] = useState(1);
	const [quoteId, setQuoteId] = useState<number | null>(null);
	const [quoteType, setQuoteType] = useState<string>(
		requestedQuoteType === "Bulk" ? "Bulk" : "All",
	);

	// User-filter selection (LTL AR / LAE / Customer) lives in the shared atom;
	const { ltlBrokerIds, primaryBrokerIds, customerIds } = useSharedUserFilter(
		[],
	);

	const quoteBoardParams = useMemo<GetQuoteBoardType>(
		() => ({
			timeFrame,
			quoteId,
			ltlBrokers: ltlBrokerIds,
			laes: primaryBrokerIds,
			customers: customerIds,
		}),
		[timeFrame, quoteId, ltlBrokerIds, primaryBrokerIds, customerIds],
	);

	const {
		data: gridData,
		isFetching,
		refetch,
	} = useGetQuoteBoardItemsV2(
		isReady && isEnabled ? quoteBoardParams : { shouldRun: false },
	);
	const {
		activeBatch: bulkBatchStatus,
		trackedBatchId,
		isPollingTimedOut,
		dismissBatch,
		retryBatchStatus,
	} = useBulkQuoteBatchBoardStatus({
		focusedBatchId,
		refetch,
		showError,
		showWarning,
	});
	const refreshBulkBoard = useCallback(() => {
		void refetch();
	}, [refetch]);
	const handleCloseCarrierSelection = () => {
		setCarrierSelectionQuoteId(null);
		if (carrierSelectionQuoteId != null) {
			setBulkInsuranceApplied((current) => {
				const updated = { ...current };
				delete updated[carrierSelectionQuoteId];
				return updated;
			});
		}
		refreshBulkBoard();
	};
	const hasBulkInsurance = useCallback(
		(quoteId: number) => {
			const quote = (gridData ?? []).find((item) => item.quoteId === quoteId);
			return getBulkInsuranceState(bulkInsuranceApplied, quoteId, quote);
		},
		[bulkInsuranceApplied, gridData],
	);

	// Internal and external users both use the server-backed preference path
	// (visibility, order, width) via independent endpoints/hooks. Both data and
	// sync hooks are called unconditionally; each self-disables its network
	// request for the other deployment, and the active set is selected by
	// deployment type. The external flow is left fully untouched.
	const { data: externalUserPreferences } = useGetUserPreferences(
		GridIdentifiers["Quote Board"],
	);
	const { data: internalUserPreferences } = useGetInternalUserPreferences(
		GridIdentifiers["Quote Board"],
	);
	const userPreferences = isInternal
		? internalUserPreferences
		: externalUserPreferences;
	const savedQuoteBoardColumns = userPreferences?.columns;

	const selectedColumns = useQuoteBoardColumns(
		getQuoteBoardColumnOptions({
			isLoading: isFetching,
			isInternal,
			columnPrefs: savedQuoteBoardColumns,
			quoteType,
			isBulkTenderAvailable,
			hasInsurance: hasBulkInsurance,
			onOpenCarrierSelection: handleOpenCarrierSelection,
			onOpenBulkCarrierSelection: getEnabledQuoteAction(
				isBulkTenderAvailable,
				handleOpenCarrierSelection,
			),
			onOpenInsurance: getEnabledQuoteAction(
				isBulkTenderAvailable,
				handleOpenInsurance,
			),
		}),
	);

	const gridPrefsOptions = {
		defaultPrefs: getDefaultQuoteBoardGridPrefs(isInternal, quoteType)[
			GridIdentifiers["Quote Board"]
		]?.columns,
	};
	const externalGridPrefs = useGridPreferencesSync(
		GridIdentifiers["Quote Board"],
		gridPrefsOptions,
	);
	const internalGridPrefs = useInternalGridPreferencesSync(
		GridIdentifiers["Quote Board"],
		gridPrefsOptions,
	);
	const {
		gridKey: prefsGridKey,
		onColumnVisibilityChange: handleToggleColumn,
		onColumnReorder: handleColumnReorder,
		onColumnResize: handleColumnResize,
		resetColumns: handleResetColumns,
	} = isInternal ? internalGridPrefs : externalGridPrefs;

	useEffect(() => {
		if (
			!isMobile &&
			shouldResetQuoteBoardDefaults(savedQuoteBoardColumns, quoteType)
		) {
			handleResetColumns();
		}
	}, [handleResetColumns, isMobile, quoteType, savedQuoteBoardColumns]);

	const handleSetChips = (newValue: string) => {
		setChips(
			newValue ? [{ ...DEFAULT_CHIP, text: newValue, value: newValue }] : [],
		);
	};

	const handleSelectTimeFrame = ({ value }: { value: number }) => {
		setTimeFrame(value);
	};

	const handleRefresh = () => {
		retryBatchStatus();
		setChips([]);
		setGridResetKey((prev) => prev + 1);
		refetch();
	};

	const displayedGridData = useMemo(
		() => applyBulkTenderFeatureGate(gridData ?? [], isBulkTenderAvailable),
		[gridData, isBulkTenderAvailable],
	);
	const bulkTenderSelection = useBulkTenderSelection(
		getBulkTenderSelectionItems(displayedGridData, isBulkTenderAvailable),
		{ isInternal },
	);
	const notificationTargets = useMemo<BulkTenderNotificationTarget[]>(
		() =>
			displayedGridData
				.filter((item) =>
					bulkTenderSelection.selectedQuoteIds.has(item.quoteId),
				)
				.map((item) => ({
					quoteId: item.quoteId,
					customerId: "customerId" in item ? item.customerId : null,
				})),
		[bulkTenderSelection.selectedQuoteIds, displayedGridData],
	);
	const selectedRequoteQuoteId = displayedGridData.find(
		(item) =>
			bulkTenderSelection.selectedQuoteIds.has(item.quoteId) &&
			canRequoteQuoteBoardRow(item, { isInternal }),
	)?.quoteId;
	const canTenderSelection = useMemo(
		() =>
			bulkTenderSelection.selectedCount > 0 &&
			displayedGridData
				.filter((item) =>
					bulkTenderSelection.selectedQuoteIds.has(item.quoteId),
				)
				.every((item) => canTenderQuoteBoardRow(item, { isInternal })),
		[
			bulkTenderSelection.selectedCount,
			bulkTenderSelection.selectedQuoteIds,
			displayedGridData,
			isInternal,
		],
	);
	const handleRequote = useCallback(
		(quoteId: number) => {
			requoteQuote(quoteId, {
				onSuccess: (result) => {
					if (!result.success) {
						showError(result.error);
						return;
					}
					showSuccess(result.data.message);
					bulkTenderSelection.clearSelection();
					refreshBulkBoard();
				},
				onError: () =>
					showError("Unable to requote this shipment. Please try again."),
			});
		},
		[
			bulkTenderSelection,
			refreshBulkBoard,
			requoteQuote,
			showError,
			showSuccess,
		],
	);
	const handleBulkTenderResultAction = useCallback(
		(quoteId: number, reasonCode: string) => {
			if (reasonCode !== "RERATE_REQUIRED") return;
			setShowBulkTenderModal(false);
			bulkTenderSelection.clearSelection();
			handleOpenRerate(quoteId);
		},
		[bulkTenderSelection, handleOpenRerate],
	);
	const getBulkTenderCarrierId = useCallback(
		(item: QuoteBoardItem | CustomerQuoteBoardItem) =>
			item.selectedCarrierPriceId,
		[],
	);
	const handlePreflightBulkTender = useCallback(
		() =>
			validateBulkTenderSelection(
				displayedGridData,
				[...bulkTenderSelection.selectedQuoteIds],
				getBulkTenderCarrierId,
				{ isInternal },
			),
		[
			bulkTenderSelection.selectedQuoteIds,
			displayedGridData,
			getBulkTenderCarrierId,
			isInternal,
		],
	);
	const showBulkTenderActions = shouldShowBulkTenderActions(
		displayedGridData,
		quoteType,
		isBulkTenderAvailable,
	);

	const filteredGridData = useQuoteBoardFilter(displayedGridData, chips);
	const mobileFilteredGridData = useMemo(
		() => filterByQuoteType(filteredGridData, quoteType),
		[filteredGridData, quoteType],
	);

	const renderHeader = () => {
		if (!isReady) {
			return null;
		}
		return (
			<QuoteBoardHeaderV2
				chips={chips}
				columns={selectedColumns}
				isMobile={isMobile}
				onChangeColumn={handleToggleColumn}
				onQuoteTypeChange={setQuoteType}
				onRefresh={handleRefresh}
				onResetColumns={handleResetColumns}
				onSetChips={handleSetChips}
				onSetQuoteId={setQuoteId}
				onSetTimeFrame={handleSelectTimeFrame}
				quoteType={quoteType}
			/>
		);
	};

	const renderBoard = () =>
		isMobile ? (
			<QuoteBoardCardList
				isInternal={isInternal}
				isLoading={isFetching}
				items={mobileFilteredGridData}
				onOpenBulkCarrierSelection={getEnabledQuoteAction(
					isBulkTenderAvailable,
					handleOpenCarrierSelection,
				)}
				onOpenCarrierSelection={handleOpenCarrierSelection}
				onToggleBatchSelection={bulkTenderSelection.toggleBatch}
				onToggleQuoteSelection={bulkTenderSelection.toggleQuote}
				selectedQuoteIds={bulkTenderSelection.selectedQuoteIds}
				showBulkTenderSelection={showBulkTenderActions}
			/>
		) : (
			<QuoteBoardGrid
				columns={selectedColumns}
				focusedBatchId={trackedBatchId}
				gridData={filteredGridData}
				hasActiveSearchChips={chips.length > 0}
				isInternal={isInternal}
				isLoading={isFetching}
				onColumnReorder={handleColumnReorder}
				onColumnResize={handleColumnResize}
				onToggleBatchSelection={bulkTenderSelection.toggleBatch}
				onToggleQuoteSelection={bulkTenderSelection.toggleQuote}
				prefsGridKey={prefsGridKey}
				quoteType={quoteType}
				resetKey={gridResetKey}
				selectedQuoteIds={bulkTenderSelection.selectedQuoteIds}
				showBulkTenderSelection={showBulkTenderActions}
				tenderedQuoteIds={tenderedQuoteIds}
			/>
		);

	return (
		<TraxPageLayout>
			<TraxPageHeader
				isLtlOnly={!isInternal}
				representativeType={isInternal ? undefined : "ltl"}
				showSupportMessage={!isInternal}
				title={"Quote Board"}
			/>
			<div className={"quote-board-content"}>
				{renderHeader()}
				{bulkBatchStatus || isPollingTimedOut ? (
					<BulkQuoteBatchStatus
						errorCount={bulkBatchStatus?.errorCount ?? undefined}
						isPollingTimedOut={isPollingTimedOut}
						onDismiss={dismissBatch}
						onRetry={retryBatchStatus}
						processedCount={bulkBatchStatus?.processedCount ?? undefined}
						requestCount={bulkBatchStatus?.requestCount ?? undefined}
						status={bulkBatchStatus?.status ?? undefined}
					/>
				) : null}
				{isRequoting ? (
					<RequoteStatusBanner quoteId={requotingQuoteId} />
				) : null}
				{showBulkTenderActions ? (
					<div className={getBulkTenderToolbarWrapperClassName(isMobile)}>
						<BulkTenderToolbar
							isRequoting={isRequoting}
							onRequote={
								selectedRequoteQuoteId == null
									? undefined
									: () => handleRequote(selectedRequoteQuoteId)
							}
							onTenderShipments={() => setShowBulkTenderModal(true)}
							rerateTitle={getBulkRerateTitle(
								bulkTenderSelection.selectedCount,
							)}
							selectedCount={bulkTenderSelection.selectedCount}
							tenderDisabled={!canTenderSelection}
						/>
					</div>
				) : null}

				{renderBoard()}
			</div>
			<QuoteBoardModals
				bulkTenderEnabled={isBulkTenderAvailable}
				carrierSelectionQuoteId={carrierSelectionQuoteId}
				initialInsuranceQuote={
					insuranceQuoteId == null
						? undefined
						: displayedGridData.find(
								(item) => item.quoteId === insuranceQuoteId,
							)
				}
				insuranceQuoteId={insuranceQuoteId}
				notificationTargets={notificationTargets}
				onBulkTenderResultAction={handleBulkTenderResultAction}
				onCloseBulkModal={() => setShowBulkModal(false)}
				onCloseBulkTenderModal={() => setShowBulkTenderModal(false)}
				onCloseCarrierSelection={handleCloseCarrierSelection}
				onCloseInsurance={() => setInsuranceQuoteId(null)}
				onCompleteBulkTender={(successfulQuoteIds) => {
					setShowBulkTenderModal(false);
					if (successfulQuoteIds.length > 0) {
						setTenderedQuoteIds(successfulQuoteIds);
					}
					refreshBulkBoard();
					bulkTenderSelection.clearSelection();
				}}
				onCompleteCarrierSelection={handleCloseCarrierSelection}
				onCompleteInsurance={(insuranceApplied) => {
					applyBulkInsuranceStateChange(
						insuranceQuoteId,
						insuranceApplied,
						handleInsuranceStateChange,
					);
					setInsuranceQuoteId(null);
					refreshBulkBoard();
				}}
				onInsuranceStateChange={handleInsuranceStateChange}
				onPreflightBulkTender={handlePreflightBulkTender}
				quoteIds={[...bulkTenderSelection.selectedQuoteIds].sort(
					(left, right) => left - right,
				)}
				showBulkModal={showBulkModal}
				showBulkTenderModal={showBulkTenderModal}
				unprocessedCount={unprocessedCount}
			/>
		</TraxPageLayout>
	);
};
